import type {
  DataAuditAuditByAtsIdApiResponse,
  DataAuditAuditByAtsIdNodeApi,
  DataAuditAuditDetailApi,
} from 'src/components/data-audit/types';

import {
  DATA_AUDIT_BY_ATS_ID_ARRAY_KEYS,
  DATA_AUDIT_BY_ATS_ID_AUDIT_ARRAY_KEYS,
} from './constants';


function extractDataAuditAuditByAtsIdRawItems(
  response: unknown,
): Array<unknown> {
  if (Array.isArray(response)) {
    return response;
  }

  if (!response || typeof response !== 'object') {
    return [];
  }

  const record = response as Record<string, unknown>;

  if (isDataAuditAuditDetailApi(record)) {
    return [record];
  }

  for (const key of DATA_AUDIT_BY_ATS_ID_ARRAY_KEYS) {
    const value = record[key];
    if (Array.isArray(value)) {
      return value;
    }
  }

  return [];
}

function isDataAuditAuditDetailApi(
  value: unknown,
): value is DataAuditAuditDetailApi {
  return (
    !!value &&
    typeof value === 'object' &&
    'auditId' in value &&
    typeof (value as {auditId: unknown}).auditId === 'string'
  );
}

function normalizeDataAuditAuditDetailApi(
  audit: DataAuditAuditDetailApi,
): DataAuditAuditDetailApi {
  return {
    ...audit,
    httpAudits: audit.httpAudits ?? [],
  };
}

function extractAuditsFromWritebackNode(
  item: Record<string, unknown>,
): Array<DataAuditAuditDetailApi> {
  for (const key of DATA_AUDIT_BY_ATS_ID_AUDIT_ARRAY_KEYS) {
    const value = item[key];
    if (Array.isArray(value)) {
      return value
        .filter(isDataAuditAuditDetailApi)
        .map(normalizeDataAuditAuditDetailApi);
    }
  }

  if (isDataAuditAuditDetailApi(item)) {
    return [normalizeDataAuditAuditDetailApi(item)];
  }

  return [];
}

function normalizeDataAuditAuditByAtsIdNode(
  item: unknown,
): DataAuditAuditByAtsIdNodeApi {
  if (!item || typeof item !== 'object') {
    return {
      writebackId: null,
      queueId: null,
      audits: [],
    };
  }

  const record = item as Record<string, unknown>;
  const writebackId =
    (record.writebackId as string | null | undefined) ??
    (record.writeback_id as string | null | undefined) ??
    null;
  const queueId =
    (record.queueId as string | null | undefined) ??
    (record.queue_id as string | null | undefined) ??
    (record.id as string | null | undefined) ??
    null;

  return {
    writebackId,
    queueId,
    audits: extractAuditsFromWritebackNode(record),
  };
}

function groupDataAuditAuditsByWritebackOrQueue(
  audits: Array<DataAuditAuditDetailApi>,
): DataAuditAuditByAtsIdApiResponse {
  const groupedNodes = new Map<string, DataAuditAuditByAtsIdNodeApi>();

  audits.forEach((audit) => {
    const normalizedAudit = normalizeDataAuditAuditDetailApi(audit);
    const groupKey = normalizedAudit.writebackId || normalizedAudit.auditId;
    const existingNode = groupedNodes.get(groupKey);

    if (existingNode) {
      existingNode.audits.push(normalizedAudit);
      return;
    }

    groupedNodes.set(groupKey, {
      writebackId: normalizedAudit.writebackId || null,
      queueId: null,
      audits: [normalizedAudit],
    });
  });

  return Array.from(groupedNodes.values());
}

export function normalizeDataAuditAuditByAtsIdResponse(
  response: unknown,
): DataAuditAuditByAtsIdApiResponse {
  const rawItems = extractDataAuditAuditByAtsIdRawItems(response);

  if (rawItems.length === 0) {
    return [];
  }

  if (isDataAuditAuditDetailApi(rawItems[0])) {
    return groupDataAuditAuditsByWritebackOrQueue(
      rawItems.filter(isDataAuditAuditDetailApi),
    );
  }

  return rawItems
    .map((item) => normalizeDataAuditAuditByAtsIdNode(item))
    .filter(
      (node) => node.writebackId || node.queueId || node.audits.length > 0,
    );
}

export function getDataAuditAuditByAtsIdNodeKey(
  node: DataAuditAuditByAtsIdNodeApi,
  index: number,
): string {
  return `${node.writebackId ?? ''}:${node.queueId ?? ''}:${index}`;
}
